/**
 * Mass-edit batch apply — turns the grid's pending `MassEdits` into the batch
 * request envelopes the backend accepts (BE↔DS service contract §3.1) and
 * dispatches them.
 *
 * The mass-edit surface stores the *final desired value* per field, while the
 * recording-management API is op-based (add/delete/edit). This module does the
 * diff against each row's current value — mirroring the Recording Edit Form's
 * save logic (RecordingEditForm.handleSaveRecordingDetails) so the two surfaces
 * produce identical payloads — and groups the result into the three batch
 * endpoints:
 *
 *   • correct recording data      (contract 4.1.1) — title, duration, live,
 *                                                    version, isrcs, year /
 *                                                    country of recording,
 *                                                    (P) names
 *   • change associated artists   (contract 4.1.3) — artist add/remove/demote
 *   • manage contributions        (contract 4.2.1) — contribution add/edit/remove
 *
 * Each endpoint takes a *list* of envelopes, one per recording, so a mass edit
 * spanning N recordings is at most three HTTP calls rather than 3N. The
 * response is an aggregate `{success, results[]}` where `results` carries one
 * per-recording outcome, letting us report exactly which recordings failed.
 *
 * Newly created artists are the one exception: `create artist and link`
 * (contract 4.4.2) has no batch form, so those are fanned out per recording
 * before the artists batch, exactly as the edit form does.
 */

import type { PortalApi as AdminPortalApi } from 'shared_components/generated/admin';
import {
  ChangeAssociatedArtistsRequestEditsInnerFeaturedStatusEnum,
  ChangeAssociatedArtistsRequestEditsInnerOpEnum,
  ChangeAssociatedArtistsRequestToJSON,
  CorrectRecordingRequestEditsInnerOpEnum,
  CorrectRecordingRequestToJSON,
  CreateArtistAndLinkRequestEditsInnerFeaturedStatusEnum,
  CreateArtistAndLinkRequestEditsInnerOpEnum,
  CreateArtistAndLinkRequestEditsInnerSourceEnum,
  ManageContributionsRequestEditsInnerOpEnum,
  ManageContributionsRequestToJSON,
} from 'shared_components/generated/admin';
import type {
  BatchServiceResponse,
  ChangeAssociatedArtistsRequest,
  ChangeAssociatedArtistsRequestEditsInner,
  CorrectRecordingRequest,
  CorrectRecordingRequestEditsInner,
  CreateArtistAndLinkRequestEditsInner,
  ManageContributionsRequest,
  ManageContributionsRequestEditsInner,
} from 'shared_components/generated/admin';
import type { Row } from './MasterDiscographyTypes';
import type { ArtistEntry } from './Components/ArtistEditorModal';
import {
  seedArtistsFromRow,
  type MassEdits,
  type MassRowEdits,
} from './massEditModel';
import { friendlySaveError } from './Components/saveErrorMessage';
import {
  contributionAliasProblem,
  hasContributionAlias,
  liveToString,
} from '../../utils/recordingValidators';

/** ISRCs reassigned onto a recording, keyed by ISRC → the recordings they were
 *  taken from. Needed so `dup_isrcs` can be sent with the `add` op. */
export type IsrcReassignments = Record<
  string,
  { recordingId: string; documentId: string }[]
>;

/** Per-recording reassignment map, keyed by the recording gaining the ISRC. */
export type MassIsrcReassignments = Record<string, IsrcReassignments>;

/** One envelope of a batch request (contract §3.1). Serialised to snake_case
 *  by the generated `*ToJSON` helpers before it goes on the wire. */
interface BatchEnvelope {
  recording_id: string;
  document_id?: string | null;
  payload: unknown;
}

export interface MassEditApplyResult {
  /** True when every recording in every batch applied cleanly. */
  ok: boolean;
  /** Recording ids that applied cleanly (safe to update optimistically). */
  appliedRecordingIds: string[];
  /** Recording id → human-readable failure reasons. */
  errorsByRecording: Record<string, string[]>;
  /** A transport/validation failure that aborted a whole batch (no per-recording
   *  detail is available in that case). */
  fatalError?: string;
}

/* -------------------------------------------------------------------------- */
/* Payload builders                                                            */
/* -------------------------------------------------------------------------- */

/** Identity used to match an edited artist against the recording's current
 *  artists. Ids are authoritative; names are the fallback for legacy rows whose
 *  `mainArtists` are plain strings. */
const artistKey = (a: { id?: string; name: string }) => a.id || a.name;

/**
 * Reconcile a requested version list against the one the recording already has.
 *
 * Versions are rename-only: a recording's existing versions occupy fixed slots
 * that can be renamed but never removed or blanked out. So a slot the caller
 * left blank, or omitted by passing a shorter list, keeps the value it had —
 * which is what makes an unpaired delete op unrepresentable downstream, no
 * matter what a calling surface hands us. Entries past the end of `original`
 * are genuine additions and survive (mass edit never sends any; the recording
 * edit form builds its own payload).
 */
export function reconcileVersions(
  original: string[],
  requested: string[]
): string[] {
  return [
    ...original.map((before, i) => requested[i]?.trim() || before),
    ...requested
      .slice(original.length)
      .map((v) => v.trim())
      .filter(Boolean),
  ];
}

/**
 * Build the `correct recording data` edit list for one recording (4.1.1).
 * Returns an empty array when none of that endpoint's fields changed.
 */
export function buildCorrectRecordingEdits(
  row: Row,
  edits: MassRowEdits,
  reassignments?: IsrcReassignments
): CorrectRecordingRequestEditsInner[] {
  const out: CorrectRecordingRequestEditsInner[] = [];

  if (edits.title !== undefined) {
    out.push({
      op: CorrectRecordingRequestEditsInnerOpEnum.Edit,
      field: 'recordingTitle',
      value: edits.title as unknown as object,
    });
  }

  if (edits.duration !== undefined) {
    out.push({
      op: CorrectRecordingRequestEditsInnerOpEnum.Edit,
      field: 'duration',
      value: edits.duration as unknown as object,
    });
  }

  if (edits.live !== undefined) {
    // Only the `live` descriptor string is persisted — the boolean `is_live`
    // flag is derived downstream from its presence (edit-form parity).
    out.push({
      op: CorrectRecordingRequestEditsInnerOpEnum.Edit,
      field: 'live',
      value: liveToString(edits.live) as unknown as object,
    });
  }

  if (edits.version !== undefined) {
    // recordingVersion is list[str]: emit a delete per removed value and an add
    // per new one rather than a whole-list edit.
    const original = row.versionsArray ?? [];
    const requested = reconcileVersions(original, edits.version);
    original
      .filter((v) => !requested.includes(v))
      .forEach((v) =>
        out.push({
          op: CorrectRecordingRequestEditsInnerOpEnum.Delete,
          field: 'recordingVersion',
          value: v as unknown as object,
        })
      );
    requested
      .filter((v) => !original.includes(v))
      .forEach((v) =>
        out.push({
          op: CorrectRecordingRequestEditsInnerOpEnum.Add,
          field: 'recordingVersion',
          value: v as unknown as object,
        })
      );
  }

  if (edits.isrcs !== undefined) {
    const original = row.isrcsArray ?? [];
    edits.isrcs
      .filter((v) => !original.includes(v))
      .forEach((v) => {
        // An ISRC taken from another recording carries the source recordings so
        // the backend can detach it there in the same operation.
        const dupSources = reassignments?.[v];
        out.push({
          op: CorrectRecordingRequestEditsInnerOpEnum.Add,
          field: 'isrcs',
          value: v as unknown as object,
          dupIsrcs:
            dupSources && dupSources.length > 0
              ? dupSources.map((d) => ({
                  dupRecordingId: d.recordingId,
                  dupDocumentId: d.documentId,
                }))
              : undefined,
        });
      });
    original
      .filter((v) => !edits.isrcs!.includes(v))
      .forEach((v) =>
        out.push({
          op: CorrectRecordingRequestEditsInnerOpEnum.Delete,
          field: 'isrcs',
          value: v as unknown as object,
        })
      );
  }

  // Year of Recording / Country of Recording are scalar edits; an emptied
  // value sends '' so the backend clears it (edit-form parity). The wire
  // field for the grid's `location` column is `countryOfRecording`.
  if (edits.yearRecorded !== undefined) {
    out.push({
      op: CorrectRecordingRequestEditsInnerOpEnum.Edit,
      field: 'yearRecorded',
      value: edits.yearRecorded.trim() as unknown as object,
    });
  }

  if (edits.location !== undefined) {
    out.push({
      op: CorrectRecordingRequestEditsInnerOpEnum.Edit,
      field: 'countryOfRecording',
      value: edits.location as unknown as object,
    });
  }

  if (edits.pNames !== undefined) {
    // pNames is list[str] in the OS schema: one delete per removed name and
    // one add per new one, in that order (edit-form parity).
    const original = row.pNames ?? [];
    original
      .filter((v) => !edits.pNames!.includes(v))
      .forEach((v) =>
        out.push({
          op: CorrectRecordingRequestEditsInnerOpEnum.Delete,
          field: 'pNames',
          value: v as unknown as object,
        })
      );
    edits.pNames
      .filter((v) => !original.includes(v))
      .forEach((v) =>
        out.push({
          op: CorrectRecordingRequestEditsInnerOpEnum.Add,
          field: 'pNames',
          value: v as unknown as object,
        })
      );
  }

  return out;
}

/**
 * Build the `change associated artists` edit list for one recording (4.1.3).
 *
 * Artists flagged `pendingCreate` are deliberately excluded — they don't exist
 * in the graph yet and are linked through `create artist and link` instead (see
 * `buildCreateArtistEdits`).
 */
export function buildArtistEdits(
  row: Row,
  edits: MassRowEdits
): ChangeAssociatedArtistsRequestEditsInner[] {
  if (edits.artist === undefined) return [];

  const original = seedArtistsFromRow(row);
  const originalKeys = original.map(artistKey);
  const current = edits.artist.filter((a) => a.name.trim());
  const currentKeys = current.map(artistKey);
  const out: ChangeAssociatedArtistsRequestEditsInner[] = [];

  // Added — new to the recording and already existing in the graph.
  current
    .filter((a) => !originalKeys.includes(artistKey(a)) && !a.pendingCreate)
    .forEach((a) => {
      out.push({
        artistId: a.id || a.name,
        op: ChangeAssociatedArtistsRequestEditsInnerOpEnum.Add,
        // The contract only accepts fa|ofa on add; 'nf' is the editor's
        // non-featured marker and maps onto "other featured artist".
        featuredStatus:
          a.featuredStatus === 'fa'
            ? ChangeAssociatedArtistsRequestEditsInnerFeaturedStatusEnum.Fa
            : ChangeAssociatedArtistsRequestEditsInnerFeaturedStatusEnum.Ofa,
        stageName: a.name,
        // A linked artist's lineup was read off the graph, so a member with
        // no flag is one that exists. Same default as `bulkEditBatch`.
        members: (a.members ?? []).map((m) => ({
          id: m.id,
          stageName: m.stageName,
          _exists: m.exists ?? true,
        })),
      });
    });

  // Demoted — still linked, but downgraded to non-featured. Sent as delete+nfa.
  current
    .filter((a) => a.demoted && originalKeys.includes(artistKey(a)))
    .forEach((a) => {
      out.push({
        artistId: a.id || a.name,
        op: ChangeAssociatedArtistsRequestEditsInnerOpEnum.Delete,
        featuredStatus:
          ChangeAssociatedArtistsRequestEditsInnerFeaturedStatusEnum.Nfa,
        type: a.type,
      });
    });

  // Removed — dropped from the recording entirely. `featuredStatus: null`
  // distinguishes a full removal from a demotion.
  original
    .filter((a) => !currentKeys.includes(artistKey(a)))
    .forEach((a) => {
      out.push({
        artistId: a.id || a.name,
        op: ChangeAssociatedArtistsRequestEditsInnerOpEnum.Delete,
        featuredStatus: null as never,
        type: a.type,
      });
    });

  return out;
}

/** Artists the user picked from a source (Discogs / MusicBrainz) that don't
 *  exist in the graph yet — linked via 4.4.2 before the artists batch. */
export function buildCreateArtistEdits(
  row: Row,
  edits: MassRowEdits
): CreateArtistAndLinkRequestEditsInner[] {
  if (edits.artist === undefined) return [];
  const originalNames = seedArtistsFromRow(row).map((a) => a.name);
  return edits.artist
    .filter(
      (
        a
      ): a is ArtistEntry & {
        pendingCreate: NonNullable<ArtistEntry['pendingCreate']>;
      } => !!a.pendingCreate && !originalNames.includes(a.name)
    )
    .map((a) => ({
      op: CreateArtistAndLinkRequestEditsInnerOpEnum.Add,
      stageName: a.name,
      source: a.pendingCreate
        .source as CreateArtistAndLinkRequestEditsInnerSourceEnum,
      sourceId: a.pendingCreate.sourceId,
      featuredStatus:
        a.featuredStatus === 'fa'
          ? CreateArtistAndLinkRequestEditsInnerFeaturedStatusEnum.Fa
          : CreateArtistAndLinkRequestEditsInnerFeaturedStatusEnum.Ofa,
      members:
        a.members && a.members.length > 0
          ? a.members.map((m) => ({
              id: m.id,
              stageName: m.stageName,
              _exists: m.exists ?? false,
            }))
          : undefined,
    }));
}

/** Parse the evidence/notes JSON blob stored on a contribution's
 *  `confidencePattern` (edit-form parity). Shared with `bulkEditBatch.ts` so
 *  both surfaces unpack the blob the same way. */
export function parseEvidenceNotes(confidencePattern?: string): {
  evidenceUrl: string;
  notes: string;
} {
  if (!confidencePattern?.trim()) return { evidenceUrl: '', notes: '' };
  try {
    const parsed: unknown = JSON.parse(confidencePattern);
    if (typeof parsed === 'object' && parsed && !Array.isArray(parsed)) {
      const obj = parsed as Record<string, unknown>;
      return {
        evidenceUrl: typeof obj.evidenceUrl === 'string' ? obj.evidenceUrl : '',
        notes: typeof obj.notes === 'string' ? obj.notes : '',
      };
    }
  } catch {
    // Not a JSON payload (e.g. a plain "manual_update" marker) — no evidence.
  }
  return { evidenceUrl: '', notes: '' };
}

/**
 * Build the `manage contributions` edit list for one recording (4.2.1).
 *
 * `problems` collects any contribution that would go out without an alias.
 * That is never sendable: the backend rewrites the contribution edge with
 * `source_id = persona_id` for `edit` as well as `add`, so an alias-less edit
 * writes an edge with an empty source rather than failing (see
 * `hasContributionAlias`). Offending entries are left out of the returned list
 * *and* named in `problems`, so the caller aborts rather than silently sending
 * a subset.
 */
export function buildContributionEdits(
  row: Row,
  edits: MassRowEdits,
  problems: string[] = []
): ManageContributionsRequestEditsInner[] {
  if (edits.contributions === undefined) return [];

  const original = row.contributions ?? [];
  const current = edits.contributions;
  const out: ManageContributionsRequestEditsInner[] = [];

  /** Returns null (and records a problem) for an entry with no alias, so the
   *  `add` and `edit` branches below can share one gate. */
  const toEdit = (
    c: (typeof current)[number],
    op: ManageContributionsRequestEditsInnerOpEnum
  ): ManageContributionsRequestEditsInner | null => {
    const role = c.contribution ?? '';
    const personaId = (c as { personaId?: string }).personaId;
    if (!hasContributionAlias(personaId)) {
      problems.push(contributionAliasProblem(role));
      return null;
    }
    const { evidenceUrl, notes } = parseEvidenceNotes(
      c.confidencePattern ?? ''
    );
    return {
      op,
      role,
      personaId,
      dataCategory: c.confidenceCategory ?? '1',
      sourceUrl: evidenceUrl || undefined,
      notes: notes || undefined,
    };
  };
  const pushEdit = (
    c: (typeof current)[number],
    op: ManageContributionsRequestEditsInnerOpEnum
  ) => {
    const edit = toEdit(c, op);
    if (edit) out.push(edit);
  };

  current
    .filter((c) => !original.some((oc) => oc.contribution === c.contribution))
    .forEach((c) =>
      pushEdit(c, ManageContributionsRequestEditsInnerOpEnum.Add)
    );

  original
    .filter((oc) => !current.some((c) => c.contribution === oc.contribution))
    .forEach((oc) =>
      out.push({
        op: ManageContributionsRequestEditsInnerOpEnum.Delete,
        role: oc.contribution ?? '',
      })
    );

  // Same role, changed category / evidence / persona.
  current
    .filter((c) => {
      const orig = original.find((oc) => oc.contribution === c.contribution);
      return (
        !!orig &&
        (orig.confidenceCategory !== c.confidenceCategory ||
          orig.confidencePattern !== c.confidencePattern ||
          (orig as { personaId?: string }).personaId !==
            (c as { personaId?: string }).personaId)
      );
    })
    .forEach((c) =>
      pushEdit(c, ManageContributionsRequestEditsInnerOpEnum.Edit)
    );

  return out;
}

/* -------------------------------------------------------------------------- */
/* Batch assembly                                                              */
/* -------------------------------------------------------------------------- */

/** The three batch bodies plus the per-recording create-artist calls, ready to
 *  dispatch. Exported for testing and for previewing what will be sent. */
export interface MassEditBatches {
  correct: BatchEnvelope[];
  artists: BatchEnvelope[];
  contributions: BatchEnvelope[];
  creates: {
    recordingId: string;
    documentId?: string;
    edits: CreateArtistAndLinkRequestEditsInner[];
  }[];
  /** Recordings touched by at least one batch. */
  recordingIds: string[];
  /** Per-recording reasons an edit could not be built (currently: a
   *  contribution with no alias). A recording named here is not dispatched —
   *  `applyMassEdits` reports it as a failure rather than sending a partial
   *  set of its edits. */
  problemsByRecording: Record<string, string[]>;
}

export function buildMassEditBatches(
  edits: MassEdits,
  rows: Row[],
  isrcReassignments: MassIsrcReassignments = {}
): MassEditBatches {
  const rowById = new Map(rows.map((r) => [r.id, r]));
  const batches: MassEditBatches = {
    correct: [],
    artists: [],
    contributions: [],
    creates: [],
    recordingIds: [],
    problemsByRecording: {},
  };
  const touched = new Set<string>();

  Object.entries(edits).forEach(([recordingId, rowEdits]) => {
    const row = rowById.get(recordingId);
    // A pending edit for a row that's no longer on the page can't be diffed
    // safely — skip it rather than send a payload built from stale state.
    if (!row) return;

    const documentId = row.documentId;

    // Built first, and gating everything else: a recording whose contributions
    // can't be expressed is skipped whole. Sending its title/artist edits while
    // dropping the alias-less contribution would half-apply what the user asked
    // for and still report success.
    const contributionProblems: string[] = [];
    const contributionEdits = buildContributionEdits(
      row,
      rowEdits,
      contributionProblems
    );
    if (contributionProblems.length > 0) {
      batches.problemsByRecording[recordingId] = contributionProblems;
      return;
    }

    const correctEdits = buildCorrectRecordingEdits(
      row,
      rowEdits,
      isrcReassignments[recordingId]
    );
    if (correctEdits.length > 0) {
      const payload: CorrectRecordingRequest = {
        edits: correctEdits,
        documentId,
      };
      batches.correct.push({
        recording_id: recordingId,
        document_id: documentId,
        payload: CorrectRecordingRequestToJSON(payload),
      });
      touched.add(recordingId);
    }

    const createEdits = buildCreateArtistEdits(row, rowEdits);
    if (createEdits.length > 0) {
      batches.creates.push({ recordingId, documentId, edits: createEdits });
      touched.add(recordingId);
    }

    const artistEdits = buildArtistEdits(row, rowEdits);
    if (artistEdits.length > 0) {
      const payload: ChangeAssociatedArtistsRequest = {
        edits: artistEdits,
        documentId,
      };
      batches.artists.push({
        recording_id: recordingId,
        document_id: documentId,
        payload: ChangeAssociatedArtistsRequestToJSON(payload),
      });
      touched.add(recordingId);
    }

    if (contributionEdits.length > 0) {
      const payload: ManageContributionsRequest = {
        edits: contributionEdits,
        documentId,
      };
      batches.contributions.push({
        recording_id: recordingId,
        document_id: documentId,
        payload: ManageContributionsRequestToJSON(payload),
      });
      touched.add(recordingId);
    }
  });

  batches.recordingIds = Array.from(touched);
  return batches;
}

/* -------------------------------------------------------------------------- */
/* Optimistic row projection                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Project a recording's applied edits back onto its grid row so the table
 * reflects the save without waiting for a refetch. Only fields that were
 * actually edited are touched.
 */
export function projectMassEditsOntoRow(row: Row, edits: MassRowEdits): Row {
  const next: Row = { ...row, isModified: true, hasManualChanges: true };

  if (edits.title !== undefined) next.title = edits.title;
  if (edits.duration !== undefined) next.duration = edits.duration;
  if (edits.live !== undefined) {
    const descriptor = liveToString(edits.live);
    next.live = descriptor;
    // The boolean grid flag is derived from the descriptor's presence.
    next.isLive = !!descriptor;
  }
  if (edits.version !== undefined) {
    // Reconciled the same way as the payload, so the grid can never show a
    // version as gone when no delete op was sent for it.
    const versions = reconcileVersions(row.versionsArray ?? [], edits.version);
    next.versionsArray = versions;
    next.version = versions.join(', ');
  }
  if (edits.isrcs !== undefined) {
    next.isrcsArray = edits.isrcs;
    next.isrcs = edits.isrcs.join(', ');
  }
  if (edits.contributions !== undefined) {
    next.contributions = edits.contributions;
  }
  if (edits.yearRecorded !== undefined) {
    next.yearRecorded = edits.yearRecorded.trim() || null;
  }
  if (edits.location !== undefined) next.location = edits.location;
  if (edits.pNames !== undefined) next.pNames = edits.pNames;
  if (edits.artist !== undefined) {
    // Demoted artists stay linked but are no longer featured, so they leave the
    // grid's artist list along with the ones that were removed outright.
    const kept = edits.artist.filter((a) => a.name.trim() && !a.demoted);
    next.artist = kept.map((a) => a.name).join(', ');
    // Artists without a known id are pending-create — the canonical id arrives
    // on the next refetch, so emit the legacy string form for now.
    next.mainArtistsRaw = kept.map((a) =>
      a.id ? { id: a.id, stageName: a.name, type: a.type ?? 'persona' } : a.name
    );
  }

  return next;
}

/* -------------------------------------------------------------------------- */
/* Dispatch                                                                    */
/* -------------------------------------------------------------------------- */

const addError = (
  errors: Record<string, string[]>,
  recordingId: string,
  message: string
) => {
  const existing = errors[recordingId] ?? [];
  if (!existing.includes(message)) {
    errors[recordingId] = [...existing, message];
  }
};

/** Fold one batch response into the running per-recording error map. */
function collectBatchErrors(
  response: BatchServiceResponse,
  errors: Record<string, string[]>,
  fallback: string
) {
  (response.results ?? []).forEach((result) => {
    if (result.success) return;
    const messages =
      result.errors && result.errors.length > 0 ? result.errors : [fallback];
    messages.forEach((m) => addError(errors, result.recordingId, m));
  });
}

/**
 * Apply every pending mass edit.
 *
 * The three batches are dispatched sequentially so that ordering constraints
 * hold (new artists are created and linked before the artist batch runs, and a
 * recording's data correction lands before its contributions). A batch that
 * fails outright — a 4xx on the envelope structure, or a network error —
 * doesn't stop the remaining batches: each is reported independently so the
 * user sees everything that went wrong in one pass.
 */
export async function applyMassEdits(
  api: AdminPortalApi,
  userId: string,
  edits: MassEdits,
  rows: Row[],
  isrcReassignments: MassIsrcReassignments = {}
): Promise<MassEditApplyResult> {
  const batches = buildMassEditBatches(edits, rows, isrcReassignments);
  const errors: Record<string, string[]> = {};
  let fatalError: string | undefined;

  // Recordings whose edits couldn't be built at all (a contribution with no
  // alias) are failures, not no-ops — seed them before anything is dispatched
  // so they're reported even when every batch that *did* go out succeeded.
  Object.entries(batches.problemsByRecording).forEach(([recordingId, msgs]) =>
    msgs.forEach((m) => addError(errors, recordingId, m))
  );

  if (batches.recordingIds.length === 0) {
    return {
      ok: Object.keys(errors).length === 0,
      appliedRecordingIds: [],
      errorsByRecording: errors,
    };
  }

  // ── 1. Correct recording data (4.1.1) ──
  if (batches.correct.length > 0) {
    try {
      const response = await api.batchCorrectRecordingBatchServiceResponse({
        userId,
        body: batches.correct,
      });
      collectBatchErrors(
        response,
        errors,
        'Recording data could not be saved.'
      );
    } catch (err) {
      const message = await friendlySaveError(err, 'recording');
      batches.correct.forEach((e) => addError(errors, e.recording_id, message));
      fatalError = message;
    }
  }

  // ── 2a. Create + link new artists (4.4.2 — no batch form, one call each) ──
  for (const create of batches.creates) {
    try {
      await api.createArtistAndLinkServiceResponse({
        userId,
        parentRecordingId: create.recordingId,
        createArtistAndLinkRequest: {
          edits: create.edits,
          documentId: create.documentId,
        },
      });
    } catch (err) {
      addError(
        errors,
        create.recordingId,
        await friendlySaveError(err, 'artists')
      );
    }
  }

  // ── 2b. Change associated artists (4.1.3) ──
  if (batches.artists.length > 0) {
    try {
      const response = await api.batchChangeArtistsBatchServiceResponse({
        userId,
        body: batches.artists,
      });
      collectBatchErrors(response, errors, 'Artists could not be saved.');
    } catch (err) {
      const message = await friendlySaveError(err, 'artists');
      batches.artists.forEach((e) => addError(errors, e.recording_id, message));
      fatalError = fatalError ?? message;
    }
  }

  // ── 3. Manage contributions (4.2.1) ──
  if (batches.contributions.length > 0) {
    try {
      const response = await api.batchManageContributionsBatchServiceResponse({
        userId,
        body: batches.contributions,
      });
      collectBatchErrors(response, errors, 'Contributions could not be saved.');
    } catch (err) {
      const message = await friendlySaveError(err, 'contributions');
      batches.contributions.forEach((e) =>
        addError(errors, e.recording_id, message)
      );
      fatalError = fatalError ?? message;
    }
  }

  const failed = new Set(Object.keys(errors));
  const appliedRecordingIds = batches.recordingIds.filter(
    (id) => !failed.has(id)
  );

  return {
    ok: failed.size === 0,
    appliedRecordingIds,
    errorsByRecording: errors,
    fatalError,
  };
}
